// Port of SuiteModel/StyleSheet.swift: the project's style sheet, as a copy-editor keeps one for a book — the marks
// dialogue takes, how dashes are set, how numbers are written, and the forms chosen in the consistency pass. Each is null
// (or empty) until the writer chooses, and the checks then follow what the manuscript mostly does. Flat keys in the
// project's settings (`styleSheet.dialogueQuotes`, `styleSheet.numbers`, `styleSheet.choice.<id>`; the dash style is the
// older `dashStyle`).
import type { ProjectRecord, StoryEntity } from '../model/documents.ts';
import type { GlossaryEntry } from '../story/glossary.ts';
import type { QuoteStyle } from '../text/dialogueBalance.ts';
import { dashStyleExample, dashStyleName, projectDashStyle, type DashStyle } from './dashStyle.ts';
import { readStyleChoices, type VariantChoice } from './variants.ts';

/** `NumberStyle`: numbers in narration spelled out to one hundred, or as digits. */
export type NumberStyle = 'words' | 'digits';
export const NUMBER_STYLES: readonly NumberStyle[] = ['words', 'digits'];
export const numberStyleName = (s: NumberStyle) => (s === 'words' ? 'Spelled out to one hundred' : 'As digits');

export interface StyleSheet {
  dialogueQuotes: QuoteStyle | null;
  dashStyle: DashStyle | null;
  numbers: NumberStyle | null;
  /** The forms chosen in the consistency pass (`styleSheet.choice.<id>`). */
  choices: VariantChoice[];
}

export const DIALOGUE_QUOTES_KEY = 'styleSheet.dialogueQuotes';
export const NUMBERS_KEY = 'styleSheet.numbers';

/** `ProjectDocument.styleSheet()`. */
export function projectStyleSheet(project: ProjectRecord): StyleSheet {
  const quotes = project.settings[DIALOGUE_QUOTES_KEY];
  const numbers = project.settings[NUMBERS_KEY];
  return {
    dialogueQuotes: quotes === 'double' || quotes === 'single' ? quotes : null,
    dashStyle: projectDashStyle(project),
    numbers: numbers === 'words' || numbers === 'digits' ? numbers : null,
    choices: readStyleChoices(project.settings),
  };
}

/** How names and words sort in the exported style sheet: by their letters, case and accents aside (`StyleSheetDocument.key`). */
const sortKey = (s: string) => s.normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();
const before = (a: string, b: string) => {
  const x = sortKey(a), y = sortKey(b);
  return x !== y ? (x < y ? -1 : 1) : a < b ? -1 : a > b ? 1 : 0;
};
const trimSpaces = (s: string) => s.replace(/^[ \t]+|[ \t]+$/g, '');

/** `StyleSheetDocument.markdown` (Revise ▸ Style Sheet ▸ Export…): the style sheet as a copy-editor would hand it on — the
 *  house style, the forms chosen in the consistency pass, the cast's names and the glossary's words, as Markdown.
 *  `dialogue` and `dashes` are what the book is checked against (the style sheet's, else what it mostly does: said so). */
export function styleSheetMarkdown(o: {
  title: string; author: string | null; sheet: StyleSheet; dialogue: QuoteStyle; dashes: DashStyle; locale: string;
  entities: readonly Pick<StoryEntity, 'kind' | 'name' | 'aliases'>[]; glossary: readonly Pick<GlossaryEntry, 'term' | 'kind' | 'variants' | 'note'>[];
}): string {
  const lines = [`# Style Sheet: ${o.title}`, ''];
  if (o.author) lines.push(`By ${o.author}`, '');
  const usual = ' (as the book mostly has it)';
  lines.push('## House style', '',
    '- **Dialogue:** ' + (o.dialogue === 'double' ? 'double quotes “ ”' : 'single quotes ‘ ’') + (o.sheet.dialogueQuotes === null ? usual : '')
      + '; quotes inside speech take the other marks',
    `- **Dashes:** ${dashStyleName(o.dashes).toLowerCase()}, ${dashStyleExample(o.dashes)}` + (o.sheet.dashStyle === null ? usual : ''));
  if (o.sheet.numbers) lines.push('- **Numbers:** ' + numberStyleName(o.sheet.numbers).toLowerCase());
  lines.push('- **Ranges:** an en dash, 1990–95', '- **Ellipses:** one character, …', '- **Quotation marks and apostrophes:** curly',
    '- **a.m. and p.m.:** small capitals', '- **Emphasis:** italics', `- **Language:** ${o.locale}`, '');
  const choices = o.sheet.choices.map((c) =>
    c.keep === null ? `${c.forms.join(' and ')} both` : `${c.keep}, not ${c.forms.filter((f) => f.toLowerCase() !== c.keep!.toLowerCase()).join(', ')}`);
  if (choices.length) lines.push('## Spellings', '', ...[...choices].sort(before).map((c) => `- ${c}`), '');
  const named = new Set<string>();
  for (const [kind, head] of [['character', 'Characters'], ['place', 'Places'], ['object', 'Objects'], ['motif', 'Motifs']] as const) {
    const these = o.entities.filter((e) => e.kind === kind && trimSpaces(e.name) !== '');
    if (!these.length) continue;
    lines.push(`## ${head}`, '');
    for (const e of [...these].sort((a, b) => before(a.name, b.name))) {
      named.add(sortKey(e.name));
      const aliases = e.aliases.map(trimSpaces).filter((a) => a !== '');
      for (const a of aliases) named.add(sortKey(a));
      lines.push(`- **${e.name}**` + (aliases.length ? ', also ' + aliases.join(', ') : ''));
    }
    lines.push('');
  }
  const words = o.glossary.filter((g) => g.term !== '' && !named.has(sortKey(g.term))).sort((a, b) => before(a.term, b.term));
  if (words.length) {
    lines.push('## Words', '');
    for (const g of words) {
      const others = g.variants.map(trimSpaces).filter((v) => v !== '');
      const note = g.note.trim().replaceAll('\n', ' ');
      lines.push(`- **${g.term}**` + (g.kind === 'word' ? '' : ` (${g.kind})`) + (others.length ? ', also ' + others.join(', ') : '') + (note ? `: ${note}` : ''));
    }
    lines.push('');
  }
  return lines.join('\n');
}
